import { observable, action, computed, makeAutoObservable } from 'mobx';
import {
    CategoriesAndGamesCollection,
    GameStore,
} from 'src/domains/casino/state/AppCasino/store/game-store/Game.store';
import { categoriesGameBelongsTo, getCategory, makeInsertGame, getCategoryName } from 'src/domains/casino/utils/utils';
import {
    CollectionType,
    DEFAULT_CATEGORY_NAME,
    FILTER_ALL,
    GameStudioType,
    GameType,
    InsertGameType,
    ProviderType,
} from 'src/domains/casino/models/models';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { CategoryState } from './Category.state';
import { FilterOptionType, FilterValueType } from 'src/components/CmsCasinoManagement/SearchAndFilters/FilterDropdown';
import { CasinoStoreProviderZod } from 'src/api_openapi/v2/casino-store/schemas';
import { casinoProviderUtils } from 'src/domains/casino/utils/CasinoProviderUtils';
import { CasinoDataMapper } from 'src/domains/casino/utils/adapters/CasinoStoreAdapter/CasinoDataMapper';
import { Common } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';

const addGameToMiniGamesCategory = async (
    collection: CategoriesAndGamesCollection,
    categoryId: string,
    newGameParams: InsertGameType,
    gameData: InsertGameType
): Promise<void> => {
    if (gameData.provider === 'pragmatic') {
        const newGame = await collection.addNewGame(newGameParams);
        await collection.addGameToCategory(newGame.new_game.id, categoryId);
    }
    return;
};

// Category limited to 10 games
const addGameToTrendingCategory = async (
    collection: CategoriesAndGamesCollection,
    categoryId: string,
    existingGameId: number | undefined,
    newGameParams: InsertGameType
): Promise<void> => {
    if (!collection.maxGamesInTrending) {
        if (existingGameId === undefined) {
            const newGame = await collection.addNewGame(newGameParams);
            await collection.addGameToCategory(newGame.new_game.id, categoryId);
        } else {
            if (categoriesGameBelongsTo(collection.categories, existingGameId).length < 5) {
                await collection.addGameToCategory(existingGameId, categoryId);
            }
        }
    }
    return;
};

const addNewGameToCategory = async (
    collection: CategoriesAndGamesCollection,
    categoryId: string,
    gameId: number | undefined,
    newGameParams: InsertGameType
): Promise<void> => {
    if (gameId === undefined) {
        const newGame = await collection.addNewGame(newGameParams);
        await collection.addGameToCategory(newGame.new_game.id, categoryId);
    } else {
        const categoriesGameBelongsTo: Array<number> = [];
        collection.categories.forEach((category) => {
            if (category.game_ids.includes(gameId)) {
                categoriesGameBelongsTo.push(category.id);
            }
        });
        const maxGamesAmount = collection.collection === 'virtuals' ? 1 : 5;
        if (categoriesGameBelongsTo.length < maxGamesAmount) {
            await collection.addGameToCategory(gameId, categoryId);
        }
    }
};

export class LiveGamesState {
    @observable public isDragOver: boolean = false;
    @observable public searchValue: string = '';
    @observable public isPublishedFilter: boolean = true;
    @observable public selectedProviderId: FilterValueType = null;
    @observable public selectedStudioId: FilterValueType = null;
    @observable private scrollPosition: number = 0;

    private liveGamesRef: HTMLDivElement | null = null;
    private scrollTimer: NodeJS.Timeout | null = null;
    private readonly categoryStateMap: MobxMapAutoNew<number, CategoryState>;
    public readonly collectionData: CategoriesAndGamesCollection;

    public constructor(
        private readonly gameStore: GameStore,
        public readonly collection: CollectionType,
        private readonly common: Common
    ) {
        this.categoryStateMap = new MobxMapAutoNew((categoryId) => {
            return new CategoryState(categoryId, this.collectionData);
        });
        this.collectionData = this.gameStore.getCollection(this.collection);
        makeAutoObservable(this);
    }

    public get categories(): Array<CategoryState> {
        return this.collectionData.categoryIds.map((id) => this.categoryStateMap.get(id));
    }

    public updateScrollValue = (): void => {
        if (this.scrollTimer !== null) {
            clearTimeout(this.scrollTimer);
        }

        this.scrollTimer = setTimeout(() => {
            this.scrollPosition = this.liveGamesRef?.scrollTop ?? 0;
        }, 150);
    };

    public setLiveGamesRef = (ref: HTMLDivElement | null): void => {
        this.liveGamesRef = ref;
        this.liveGamesRef?.scrollTo(0, this.scrollPosition);
    };

    @action public onDrop = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        this.disableDrag(e);
    };

    @action public onDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
    };

    @action public setGameIds = async (category: CategoryState, gamesIdsOrdered: Array<number>): Promise<void> => {
        await this.collectionData.changeGamesOrder(category.id, {
            name: category.name,
            active: category.active,
            visible_in_navigation: category.visibleInNavigation,
            visible_in_all_games: category.visibleInAllGames,
            game_ids: gamesIdsOrdered,
        });
    };

    @action public onAddNewCategory = async (): Promise<void> => {
        const response = await this.collectionData.createCategory(0, DEFAULT_CATEGORY_NAME);
        this.scrollToCategory(response.new_category.id);
    };

    private scrollToCategory = (categoryId: number): void => {
        const categoryRef = this.categoryStateMap.get(categoryId).categoryRef;
        const headerSize = 210;

        if (categoryRef === null) {
            return;
        }

        const newCategoryPosition = categoryRef.offsetTop;
        this.liveGamesRef?.scrollTo({
            top: newCategoryPosition - headerSize,
            behavior: 'smooth',
        });
    };

    @action public onDropGame = async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
        e.preventDefault();
        const dataRaw = e.dataTransfer.getData('text/plain');
        const gameData = makeInsertGame(dataRaw);

        this.isDragOver = false;

        if (gameData === null) {
            return;
        }

        const { casinoStoreEnabled } = ConfigState.get(this.common).config;
        const newGameParams = {
            launch_game_id: gameData.launch_game_id,
            name: gameData.name,
            provider: casinoStoreEnabled ? gameData.provider : casinoProviderUtils.convertSlugToName(gameData.provider),
            studio_id: gameData.studio_id,
            studio_name: gameData.studio_name,
            published: gameData.published,
            image: gameData.image,
            image_vertical: gameData.image_vertical,
            image_square: gameData.image_square,
            demo: gameData.demo,
            description: gameData.description,
            max_bet: gameData.max_bet,
            min_bet: gameData.min_bet,
            max_win: gameData.max_win,
            return_to_player: gameData.return_to_player,
            jackpot_amount: gameData.jackpot_amount,
        };

        const categoryId = this.hasSomeParentTheClass(e.target, this.collectionData.categoryIds);
        const categoryName = getCategoryName(this.collectionData.categories, categoryId);

        if (categoryId === null || categoryName === DEFAULT_CATEGORY_NAME) {
            return;
        }

        // If casino store enabled use gameData.id (launch_game_id should be undefined)
        const gameId =
            this.collectionData.games.find(
                (game) => game.launch_game_id === gameData.launch_game_id && game.provider === gameData.provider
            )?.id ?? gameData.id;

        const miniGamesCategory = getCategory(this.collectionData.miniGamesCategoryId, categoryId);
        const trendingCategory = getCategory(this.collectionData.trendingGamesCategoryId, categoryId);
        const isCasinoAndTrendingCategory = this.collectionData.collection === 'casino' && trendingCategory;

        if (miniGamesCategory) {
            await addGameToMiniGamesCategory(this.collectionData, categoryId, newGameParams, gameData);
        } else if (isCasinoAndTrendingCategory) {
            await addGameToTrendingCategory(this.collectionData, categoryId, gameId, newGameParams);
        } else {
            await addNewGameToCategory(this.collectionData, categoryId, gameId, newGameParams);
        }
    };

    @action public allowDrop = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        this.isDragOver = true;
    };

    @action public disableDrag = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        this.isDragOver = false;
    };

    @action private checkIfGameMatchSearchFilter = (searchValue: string, game: GameType): boolean => {
        return game.name.toLowerCase().includes(searchValue.toLowerCase());
    };

    @action private checkIfGameMatchPublishedFilter = (isPublishedFilter: boolean, game: GameType): boolean => {
        return game.published === isPublishedFilter;
    };

    @action private checkIfGameMatchProviderFilter = (selectedProviderId: FilterValueType, game: GameType): boolean => {
        const { casinoStoreEnabled } = ConfigState.get(this.common).config;
        return (
            selectedProviderId ===
                (casinoStoreEnabled ? game.provider : casinoProviderUtils.convertNameToSlug(game.provider)) ||
            selectedProviderId === null
        );
    };

    @action private checkIfGameMatchStudioFilter = (selectedStudioId: FilterValueType, game: GameType): boolean => {
        return selectedStudioId === game.studio_name || selectedStudioId === null;
    };

    @action public checkIfGameMatchAllFilters = (
        searchValue: string,
        isPublishedFilter: boolean,
        selectedProviderId: FilterValueType,
        selectedStudioId: FilterValueType,
        game: GameType
    ): boolean => {
        return (
            this.checkIfGameMatchSearchFilter(searchValue, game) &&
            this.checkIfGameMatchPublishedFilter(isPublishedFilter, game) &&
            this.checkIfGameMatchProviderFilter(selectedProviderId, game) &&
            this.checkIfGameMatchStudioFilter(selectedStudioId, game)
        );
    };

    public onSearchGame = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.searchValue = event.target.value;
    };

    public getGameById = (id: number): GameType | undefined => {
        return this.collectionData.games.find((game) => game.id === id);
    };

    private hasSomeParentTheClass = (e: EventTarget, categoriesIds: Array<number>): string | null => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const element = e as HTMLElement;
        if (categoriesIds.length > 0) {
            for (const id of categoriesIds) {
                if (element.classList.contains(id.toString())) {
                    return id.toString();
                }
            }
        }

        if (element.parentNode === null) return null;

        return this.hasSomeParentTheClass(element.parentNode, categoriesIds);
    };

    // Filters
    @computed public get publishedFilterValue(): FilterValueType {
        return this.isPublishedFilter ? 'published' : 'not-published';
    }

    @computed public get publishedFilterOptions(): Array<FilterOptionType> {
        return [
            {
                value: 'published',
                label: 'Published',
            },
            {
                value: 'not-published',
                label: 'Not published',
            },
        ];
    }

    @action public onPublishedFilterChange = (selectedValue: FilterValueType): void => {
        this.isPublishedFilter = selectedValue === 'published';
    };

    @computed private get providersList(): Array<ProviderType> {
        const providers: Array<ProviderType> = [];
        const providerNameTool = CasinoDataMapper.get(this.common);

        for (const game of this.collectionData.games) {
            const providerSLug = providerNameTool.formatProviderNameToSlugIfNeed(game.provider);
            const gameProvider = CasinoStoreProviderZod.safeParse(providerSLug);
            if (gameProvider.success === false) {
                console.error('gameProvider type error: ', game);
                continue;
            }

            if (providers.includes(gameProvider.data) === false) {
                providers.push(gameProvider.data);
            }
        }

        return providers.sort((a, b) => a.localeCompare(b));
    }

    @computed public get providerFilterOptions(): Array<FilterOptionType> {
        const providersListOut: Array<FilterOptionType> = [
            {
                value: FILTER_ALL,
                label: FILTER_ALL,
            },
        ];

        for (const provider of this.providersList) {
            providersListOut.push({
                label: casinoProviderUtils.convertSlugToName(provider),
                value: provider,
            });
        }

        return providersListOut;
    }

    @action public onProviderFilterChange = (selectedValue: FilterValueType): void => {
        this.selectedProviderId = selectedValue === FILTER_ALL ? null : selectedValue;
        this.selectedStudioId = null;
    };

    @computed private get studiosList(): Array<GameStudioType> {
        const { casinoStoreEnabled } = ConfigState.get(this.common).config;
        const studios: Array<GameStudioType> = [];
        const gamesFilteredByProvider = this.collectionData.games
            .filter(
                (game) =>
                    this.selectedProviderId ===
                        (casinoStoreEnabled ? game.provider : casinoProviderUtils.convertNameToSlug(game.provider)) ||
                    this.selectedProviderId === null
            )
            .filter(
                (game) =>
                    (casinoStoreEnabled ? game.provider : casinoProviderUtils.convertNameToSlug(game.provider)) !==
                    game.studio_name
            );

        for (const game of gamesFilteredByProvider) {
            if (studios.findIndex((studio) => studio.name.toLowerCase() === game.studio_name.toLowerCase()) === -1) {
                studios.push({
                    id: game.studio_name,
                    name: game.studio_name,
                });
            }
        }

        return studios.filter((studio) => studio.id !== '').sort((a, b) => a.id.localeCompare(b.id));
    }

    @computed public get studioFilterOptions(): Array<FilterOptionType> {
        const studioListOut: Array<FilterOptionType> = [
            {
                value: FILTER_ALL,
                label: FILTER_ALL,
            },
        ];

        for (const studio of this.studiosList) {
            studioListOut.push({
                label: studio.name,
                value: studio.id,
            });
        }
        return studioListOut;
    }

    @action public onStudioFilterChange = (selectedValue: FilterValueType): void => {
        this.selectedStudioId = selectedValue === FILTER_ALL ? null : selectedValue;
    };
}
